import { useState, useEffect, useMemo, useCallback } from "react";
import Button from "app/storybookComponents/Button";
import Select from "react-select";
import SimpleModal from "app/components/Modals/SimpleModal";
import { Dropdown } from "react-bootstrap";
import SimpleModalFooter from "app/components/Modals/SimpleModalFooter";
import { faCirclePlus } from "@fortawesome/pro-regular-svg-icons";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  selectAllCompanyUsersById,
  selectTeamsByTeamId,
} from "app/containers/Global/slice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getUserSubordinates,
  selectAllUserSubordinates,
} from "app/containers/UserSettings/slice";
import { selectDepartments } from "../../AdminConsole/slice";
import { ManagerCustomListItem } from "../types";
import CheckinCustomListConfirmationModal from "./CheckinCustomListConfirmationModal";
import { getTotalPeopleInSelectedEntitiesFromCustomList } from "../helpers";
import CustomListSearchComponent from "../CustomListSearchComponent";
import {
  getAllManagerAndDirectReports,
  selectManagerAndDirectReportsByUserAccountId,
} from "app/containers/AdvancedCsvUpload/slice";
import AvatarCircle from "app/components/AvatarCircle";
import { getMemberOption } from "app/components/GlobalNavbar/helpers";
import { getPeopleOrPerson } from "utils/helperFunctions";
import { WEEKLY_CHECK_IN_FEATURE_NAME } from "../constants";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  modalDescription: string;
  entitiesShowing: string[]; // ex. ['managers','teams', 'departments']
  className?: string;
  teamIds?: number[];
  departmentIds?: number[];
  managers?: ManagerCustomListItem[];
}

interface Option {
  label: string;
  value: string;
  entityType?: string;
  isDisabled?: boolean;
}

const CheckInCustomListModal = ({
  isOpen,
  onClose,
  title = "Create a Custom List",
  modalDescription,
  teamIds,
  departmentIds,
  managers,
  entitiesShowing = ["managers", "teams", "departments"],
  className = "",
}: Readonly<Props>) => {
  const dispatch = useAppDispatch();
  // ---------------------------------- Selectors ----------------------------------
  const teamsById = useAppSelector(selectTeamsByTeamId);
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const departmentsById = useAppSelector(selectDepartments);
  const allUsersSubordinates = useAppSelector(selectAllUserSubordinates);
  const managersAndDirectReportsByUserAccountId = useAppSelector(
    selectManagerAndDirectReportsByUserAccountId
  );

  // ---------------------------------- States ----------------------------------
  const [selectedManagers, setSelectedManagers] = useState<
    ManagerCustomListItem[]
  >(managers || []);
  const [selectedTeams, setSelectedTeams] = useState<number[]>(teamIds || []);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedDepartments, setSelectedDepartments] = useState<number[]>(
    departmentIds || []
  );
  const [activeDropdownOption, setActiveDropdownOption] = useState<string>(
    entitiesShowing[0] || "managers"
  );
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isEntireReportingStructure, setIsEntireReportingStructure] =
    useState(false);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  useEffect(() => {
    setSelectedIds([]);
  }, [activeDropdownOption]);

  // We want to ensure that when the user closes the modal without saving,
  // we reset the selectedIds to an empty array
  useEffect(() => {
    if (!isOpen) {
      setSelectedManagers(managers || []);
      setSelectedTeams(teamIds || []);
      setSelectedDepartments(departmentIds || []);
      setSelectedIds([]);
    }
  }, [isOpen, managers, teamIds, departmentIds]);

  useEffect(() => {
    if (!managers?.length) return;
    const userAccountIds = managers.map((m) => m.userAccountId);
    dispatch(getUserSubordinates(userAccountIds));
  }, [managers, dispatch]);

  useEffect(() => {
    dispatch(getAllManagerAndDirectReports());
  }, [dispatch]);

  const totalPeopleInSelectedEntities = useMemo(
    () =>
      getTotalPeopleInSelectedEntitiesFromCustomList(
        {
          managers: selectedManagers,
          teams: selectedTeams,
          departments: [],
          // departments: selectedDepartments,
        },
        allUsersSubordinates,
        teamsById,
        departmentsById
      ),
    [
      allUsersSubordinates,
      selectedManagers,
      selectedTeams,
      // selectedDepartments,
      teamsById,
      departmentsById,
    ]
  );

  const handleSave = () => {
    setShowConfirmationModal(true);
  };

  const getSelectedValues = (): Option[] => {
    switch (activeDropdownOption) {
      case "teams":
        return selectedIds.map((id) => ({
          label: teamsById[id]?.teamName,
          value: String(teamsById[id]?.teamId),
          entityType: "team",
        }));
      case "departments":
        return selectedIds.map((id) => ({
          label: departmentsById[id]?.name ?? "",
          value: `${departmentsById[id]?.departmentId}`,
          entityType: "department",
        }));
      case "managers":
        return selectedIds.map((id) => ({
          label: usersById[id]?.firstName + " " + usersById[id]?.lastName,
          value: String(usersById[id]?.userAccountId),
          entityType: "user",
        }));
      default:
        return [];
    }
  };

  const getSearchPlaceholder = () => {
    switch (activeDropdownOption) {
      case "teams":
        return "Search by team name";
      case "departments":
        return "Search by department name";
      case "managers":
        return "Search by manager name";
      default:
        return "";
    }
  };

  const formatOptionLabel = useCallback(
    (option: Option) => {
      if (option.value === "info")
        return (
          <div className="information-option option">
            <FontAwesomeIcon icon={["far", "info-circle"]} />
            <p>{option.label}</p>
          </div>
        );

      switch (option.entityType) {
        case "user": {
          const avatarCircle = (
            <AvatarCircle
              name={`${option.label}`}
              userAccountId={Number(option.value)}
              size="small"
            />
          );
          return getMemberOption({
            label: option.label,
            avatarCircle,
          });
        }
        case "team": {
          const teamId = Number(option.value);
          const team = teamsById[teamId];
          const avatarCircle = (
            <AvatarCircle
              name={`${option.label}`}
              size="small"
              profilePicture={team?.profilePicture}
              avatarColor={team?.avatarColor}
            />
          );
          return getMemberOption({
            label: option.label,
            avatarCircle,
          });
        }
        case "department": {
          const avatarCircle = (
            <AvatarCircle name={`${option.label}`} size="small" />
          );
          return getMemberOption({
            label: option.label,
            avatarCircle,
          });
        }
        default:
          return option.label;
      }
    },
    [teamsById]
  );

  const renderEntitySearchSection = () => {
    return (
      <div className="entity-search" style={{ marginTop: "8px" }}>
        <div className="search-input-container">
          <Select<Option, true>
            noOptionsMessage={() => null}
            placeholder={getSearchPlaceholder()}
            options={selectOptions}
            isMulti
            value={getSelectedValues()}
            onChange={(values) => {
              if (values) {
                setSelectedIds(values.map(({ value }) => Number(value)));
              }
            }}
            classNames={{
              multiValue: () => "multi-value user-option-white",
              option: () => "option-value-with-border-bottom option",
            }}
            formatOptionLabel={formatOptionLabel}
            filterOption={(option, inputValue) => {
              if (option.value === "info") return true;
              return option.data.label
                .toLowerCase()
                .includes(inputValue.toLowerCase());
            }}
          />
        </div>
      </div>
    );
  };

  const onTeamRemove = (teamId: number) =>
    setSelectedTeams((prev) => prev.filter((id) => id !== teamId));
  const onDepartmentRemove = (departmentId: number) =>
    setSelectedDepartments((prev) => prev.filter((id) => id !== departmentId));
  const onManagerRemove = (manager: ManagerCustomListItem) =>
    setSelectedManagers((prev) =>
      prev.filter((m) => m.userAccountId !== manager.userAccountId)
    );

  const onAddEntities = () => {
    switch (activeDropdownOption) {
      case "managers": {
        const newManagers = selectedIds.map((id) => ({
          userAccountId: id,
          entireReportingStructure: isEntireReportingStructure,
        }));
        setSelectedManagers((prevSelected) => [
          ...prevSelected,
          ...newManagers,
        ]);
        dispatch(getUserSubordinates(newManagers.map((m) => m.userAccountId)));
        break;
      }
      case "teams": {
        setSelectedTeams((prevSelected) => [...prevSelected, ...selectedIds]);
        break;
      }
      case "departments": {
        setSelectedDepartments((prevSelected) => [
          ...prevSelected,
          ...selectedIds,
        ]);
        break;
      }
      default:
    }
    setSelectedIds([]);
  };

  const renderDropdown = () => {
    return (
      <>
        {/* <h4>Select either to add departments, teams, or managers:</h4> */}
        <h4>Select either to add teams or managers:</h4>
        <Dropdown>
          <Dropdown.Toggle
            variant="outline-secondary"
            id="dropdown-basic"
            className="w-100 d-flex align-items-center justify-content-between"
          >
            <p style={{ color: "black" }}>
              {getDropdownString(activeDropdownOption)}
            </p>
          </Dropdown.Toggle>
          <Dropdown.Menu className="w-100">
            {entitiesShowing.map((entity, idx) => (
              <>
                <Dropdown.Item
                  key={entity}
                  onClick={() => setActiveDropdownOption(entity)}
                >
                  {getDropdownString(entity)}
                </Dropdown.Item>
                {idx !== entitiesShowing.length - 1 ? (
                  <Dropdown.Divider key={`${entity}-divider`} />
                ) : null}
              </>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </>
    );
  };

  const teamOptions = useMemo(() => {
    const teams = Object.values(teamsById);
    // Filter out any teams that are already selected
    const teamObjs = teams
      .filter((team) => {
        if (
          selectedTeams.includes(team.teamId) ||
          !team.teamMemberIds?.length
        ) {
          return false;
        }
        return true;
      })
      .map((team) => ({
        label: team.teamName,
        value: team.teamId.toString(),
        entityType: "team",
      }));
    return [
      ...teamObjs,
      {
        value: "info",
        label:
          "Don’t see a team? Only teams with at least one member will be shown here. Try searching by another name.",
        isDisabled: true,
      },
    ];
  }, [teamsById, selectedTeams]);

  // const departmentOptions = useMemo(() => {
  //   const departments = Object.values(departmentsById);
  //   // Filter out any departments that are already selected
  //   const departmentObjs = departments
  //     .filter((department) => {
  //       if (
  //         !department.companyAccountId ||
  //         selectedDepartments.includes(department.departmentId) ||
  //         !department.teamMembers?.length
  //       ) {
  //         return false;
  //       }
  //       return true;
  //     })
  //     .map((department) => ({
  //       label: department.name ?? "",
  //       value: department.departmentId.toString(),
  //       entityType: "department",
  //     }));
  //   return [
  //     ...departmentObjs,
  //     {
  //       value: "info",
  //       label:
  //         "Don’t see a department? Only departments with at least one member will be shown here. Try searching by another name.",
  //       isDisabled: true,
  //     },
  //   ];
  // }, [departmentsById, selectedDepartments]);

  const managerOptions = useMemo(() => {
    const managers = Object.values(usersById).filter(
      (user) =>
        !!managersAndDirectReportsByUserAccountId[user.userAccountId]
          ?.directReportRelationships?.length
    );

    // Filter out any managers that are already selected
    const managerObjs = managers
      .filter(
        (manager) =>
          !selectedManagers.some(
            (m) => m.userAccountId === manager.userAccountId
          )
      )
      .map((manager) => ({
        label: `${manager.firstName} ${manager.lastName}`,
        value: manager.userAccountId.toString(),
        entityType: "user",
      }));
    return [
      ...managerObjs,
      {
        value: "info",
        label:
          "Don’t see a manager? Only users with at least one direct report will be shown here. Try searching by another name or email.",
        isDisabled: true,
      },
    ];
  }, [usersById, selectedManagers, managersAndDirectReportsByUserAccountId]);

  const selectOptions = useMemo<Option[]>(() => {
    switch (activeDropdownOption) {
      case "teams":
        return teamOptions;
      // case "departments":
      //   return departmentOptions;
      case "managers":
        return managerOptions;
      default:
        return [];
    }
  }, [activeDropdownOption, managerOptions, teamOptions]);

  const renderManagerOptions = () => {
    if (activeDropdownOption !== "managers" || selectedIds.length === 0) {
      return null;
    }
    const managerText =
      selectedIds.length === 1 ? "This manager" : "These managers";
    return (
      <div className="check-in-manager-options" style={{ marginTop: "16px" }}>
        <h4>Enable for:</h4>
        <div className="check-box-container">
          <input
            type="radio"
            name="managerScope"
            value="directReports"
            checked={!isEntireReportingStructure}
            onChange={() => setIsEntireReportingStructure(false)}
            id="directReportsRadio"
          />
          <label htmlFor="directReportsRadio">
            {managerText} and their direct reports only
          </label>
        </div>
        <div className="check-box-container">
          <input
            type="radio"
            name="managerScope"
            value="subordinates"
            checked={isEntireReportingStructure}
            onChange={() => setIsEntireReportingStructure(true)}
            id="subordinatesRadio"
          />
          <label htmlFor="subordinatesRadio">
            {managerText} and their entire reporting structure
          </label>
        </div>
      </div>
    );
  };

  const renderAddSelectedEntities = () => {
    if (selectedIds.length === 0) {
      return null;
    }
    return (
      <div style={{ marginTop: "16px", marginLeft: "auto" }}>
        <Button variant="secondary-blue" onClick={onAddEntities}>
          <FontAwesomeIcon icon={faCirclePlus} className="me-2" />
          Add
        </Button>
      </div>
    );
  };

  const getDropdownString = (entity: string) => `Select ${entity}`;

  return (
    <>
      <CheckinCustomListConfirmationModal
        show={isOpen && showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
        teamIds={selectedTeams}
        // departmentIds={selectedDepartments}
        managers={selectedManagers}
        onSuccess={() => {
          setShowConfirmationModal(false);
          onClose();
        }}
        onEditButtonClick={() => setShowConfirmationModal(false)}
      />
      <SimpleModal
        show={isOpen && !showConfirmationModal}
        onHide={onClose}
        title={title}
        className={`custom-list-modal ${className}`}
        size="xl"
        enforceFocus={false}
      >
        <p className="grey-text" style={{ marginTop: "-16px" }}>
          {modalDescription}
        </p>
        <div className="warning-banner light-blue border-0 d-flex align-items-center">
          <FontAwesomeIcon icon={["far", "circle-info"]} className="me-2" />
          {/* <p> // Remember to update the departments section if departments are disabled.
            This list includes all managers, teams, and departments with{" "}
            {WEEKLY_CHECK_IN_FEATURE_NAME}s enabled by you or any managers, team
            leaders, or department leaders.
          </p> */}
          <p>
            This list includes all managers and teams for whom{" "}
            {WEEKLY_CHECK_IN_FEATURE_NAME}s have been enabled, either by you or
            another manager or team leader.
          </p>
        </div>
        <hr className="m-0" />
        <div className="entity-selection-container">
          <div className="column-gap-8px">
            {renderDropdown()}
            {renderEntitySearchSection()}
            {renderManagerOptions()}
            {renderAddSelectedEntities()}
          </div>
          <div style={{ backgroundColor: "#D9DEF4" }} />
          <div className="overflow-auto">
            <div className="column-gap-16px">
              <h4>
                You’re enabling {WEEKLY_CHECK_IN_FEATURE_NAME}s for:{" "}
                <span style={{ color: "#53565A", fontWeight: "normal" }}>
                  {totalPeopleInSelectedEntities
                    ? `${totalPeopleInSelectedEntities} ${getPeopleOrPerson(
                        totalPeopleInSelectedEntities
                      )}`
                    : "No one so far"}
                </span>
              </h4>
              <CustomListSearchComponent
                managers={selectedManagers}
                teamIds={selectedTeams}
                // departmentIds={selectedDepartments}
                onTeamRemove={onTeamRemove}
                onManagerRemove={onManagerRemove}
                onDepartmentRemove={onDepartmentRemove}
              />
            </div>
          </div>
        </div>

        <hr className="m-0 mt-auto" />
        <SimpleModalFooter
          onBackButtonClick={onClose}
          onPrimaryButtonClick={handleSave}
          backButtonText="Cancel"
          primaryButtonText="Save"
        />
      </SimpleModal>
    </>
  );
};

export default CheckInCustomListModal;
